import React, {
  createContext,
  FC,
  ReactNode,
  useCallback,
  useContext,
  useMemo,
  useState,
} from 'react'

export interface CropMode {
  enabled: boolean
  sourceDocumentId?: string
  cropRect?: {
    x: number
    y: number
    width: number
    height: number
  }
  currentPage?: number
  onCropComplete?: (
    rect:
      | {
          x: number
          y: number
          width: number
          height: number
        }
      | undefined
  ) => void
  onPageChange?: (page: number) => void
}

export interface ZoneData {
  id: string
  title: string
  zone_reference_image_id?: string
  color?: string
  drawing_bounds?: {
    x_start: number
    y_start: number
    x_end: number
    y_end: number
  }
  [key: string]: unknown
}

export interface ZoneDrawingMode {
  enabled: boolean
  isDrawingMode?: boolean
  zoneReferenceImageId?: string
  zones?: ZoneData[]
  selectedZoneId?: string
  onZoneDrawn?: (rect: {
    x_start: number
    y_start: number
    x_end: number
    y_end: number
  }) => void
  onZoneClick?: (zoneId: string) => void
  /**
   * Reports the reference image's natural pixel size once the browser has
   * loaded it. The browser measurement is the single source of truth for
   * converting the AI's normalized [0,1] zone bounds into the pixel
   * `drawing_bounds` the renderer stores (the backend never reports sizes —
   * a server-side decoder can disagree with the browser on EXIF-rotated or
   * re-encoded images). `documentId` is stamped by the REPORTING viewer so
   * the id and the measurement always describe the same image — a receiver
   * pairing the size with its own "currently selected" id can mismatch them
   * across a fast document switch.
   */
  onImageNaturalSize?: (size: {
    documentId: string
    width: number
    height: number
  }) => void
}

export interface LibraDocumentContextValue {
  selectedDocumentId: string | null
  /**
   * Open a document by Rolodex entry_id, optionally jumping to a specific page.
   * When page is provided the viewer opens at that page instead of page 1.
   */
  setSelectedDocumentId: (documentId: string | null, page?: number) => void
  unitId: string | undefined
  currentViewPage: number
  setCurrentViewPage: (page: number) => void
  /** Increments on each citation-click navigation. Used by EmbeddedDocumentPanel to trigger an imperative scrollToPage call without remounting the viewer. */
  navigationKey: number
  /** Target page for the current citation navigation — stable between scroll events. */
  citationInitialPage: number
  cropMode: CropMode
  setCropMode: React.Dispatch<React.SetStateAction<CropMode>>
  zoneDrawingMode: ZoneDrawingMode
  setZoneDrawingMode: React.Dispatch<React.SetStateAction<ZoneDrawingMode>>
}

export const LibraDocumentContext = createContext<
  LibraDocumentContextValue | undefined
>(undefined)

export interface LibraDocumentProviderProps {
  children: ReactNode
  unitId: string | undefined
}

export const LibraDocumentProvider: FC<LibraDocumentProviderProps> = ({
  children,
  unitId,
}) => {
  const [selectedDocumentId, setSelectedDocumentId] = useState<string | null>(
    null
  )
  const [currentViewPage, setCurrentViewPageState] = useState<number>(1)
  const [navigationKey, setNavigationKey] = useState<number>(0)
  const [citationInitialPage, setCitationInitialPage] = useState<number>(1)
  const [cropMode, setCropMode] = useState<CropMode>({
    enabled: false,
  })
  const [zoneDrawingMode, setZoneDrawingMode] = useState<ZoneDrawingMode>({
    enabled: false,
  })

  const handleSetSelectedDocumentId = useCallback(
    (documentId: string | null, page?: number) => {
      setSelectedDocumentId(documentId)
      setCurrentViewPageState(page ?? 1)
      if (page !== undefined) {
        setCitationInitialPage(page)
        setNavigationKey((k) => k + 1)
      } else {
        setCitationInitialPage(1)
      }
    },
    []
  )

  const handleSetCurrentViewPage = useCallback((page: number) => {
    setCurrentViewPageState(page)
  }, [])

  const value = useMemo<LibraDocumentContextValue>(
    () => ({
      selectedDocumentId,
      setSelectedDocumentId: handleSetSelectedDocumentId,
      unitId,
      currentViewPage,
      setCurrentViewPage: handleSetCurrentViewPage,
      navigationKey,
      citationInitialPage,
      cropMode,
      setCropMode,
      zoneDrawingMode,
      setZoneDrawingMode,
    }),
    [
      selectedDocumentId,
      handleSetSelectedDocumentId,
      unitId,
      currentViewPage,
      handleSetCurrentViewPage,
      navigationKey,
      citationInitialPage,
      cropMode,
      zoneDrawingMode,
    ]
  )

  return (
    <LibraDocumentContext.Provider value={value}>
      {children}
    </LibraDocumentContext.Provider>
  )
}

export const useLibraDocument = (): LibraDocumentContextValue => {
  const context = useContext(LibraDocumentContext)
  if (!context) {
    throw new Error(
      'useLibraDocument must be used within a LibraDocumentProvider'
    )
  }
  return context
}
